import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import {
  buildStationDetailsHref,
  buildStoreDetailsHref,
} from "Helpers/stationsBreadcrumbs";
import {
  pickProfileEntityTitle,
  pickStationDisplayName,
} from "Helpers/pickProfileEntityTitle";
import {
  getStationEditPath,
  useStationRouteContext,
} from "Helpers/stationRoutes";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  StationGroupProfileService,
  StationProfileService,
  StoreProfileService,
} from "subModule/src/core/services";

export function useFrozenStationEntityTitle(stationIdOverride?: number): string {
  const { stationId: ctxStationId } = useStationRouteContext();
  const params = useParams();
  const id = stationIdOverride ?? ctxStationId ?? (params.id ? +params.id : undefined);
  const [entityTitle, setEntityTitle] = useState("");

  useEffect(() => {
    if (!id) return;
    let active = true;
    StationProfileService.getStationByID(id)
      .then((res) => {
        if (!active) return;
        const name = pickStationDisplayName(res);
        if (name) setEntityTitle(name);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id]);

  return entityTitle;
}

/** Nested station tabs — Stations > List > Name > Tab */
export function useFrozenStationTabBreadcrumbs(
  actionLabelKey: string,
): BreadcrumbItem[] {
  const params = useParams();
  const { stationId, listPath, businessId, isBusinessContext } =
    useStationRouteContext();
  const id = stationId ? String(stationId) : params.id;
  const entityTitle = useFrozenStationEntityTitle();
  const detailsHref = stationId
    ? getStationEditPath(stationId, businessId)
    : buildStationDetailsHref({ id });

  return useMemo(
    () => [
      ...(isBusinessContext
        ? [
            { labelKey: "businessSetup" as const },
            { labelKey: "stations" as const, href: listPath },
          ]
        : [
            { labelKey: "stations" as const },
            { labelKey: "stationsList" as const, href: listPath || "/stations" },
          ]),
      {
        labelKey: "name",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: detailsHref,
      },
      { labelKey: actionLabelKey },
    ],
    [detailsHref, listPath, entityTitle, actionLabelKey, isBusinessContext],
  );
}

export function useFrozenStoreEntityTitle(storeId?: string): string {
  const params = useParams();
  const id = storeId ?? params.id;
  const [entityTitle, setEntityTitle] = useState("");

  useEffect(() => {
    if (!id) return;
    let active = true;
    StoreProfileService.getStoreByID(+id)
      .then((res) => {
        if (!active) return;
        const name = pickProfileEntityTitle(res);
        if (name) setEntityTitle(name);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id]);

  return entityTitle;
}

/** Nested store tabs — Stations > Stores > Name > Tab */
export function useFrozenStoreTabBreadcrumbs(
  actionLabelKey: string,
): BreadcrumbItem[] {
  const { id } = useParams();
  const entityTitle = useFrozenStoreEntityTitle();

  return useMemo(
    () => [
      { labelKey: "stations" },
      { labelKey: "stores", href: "/stores" },
      {
        labelKey: "title",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: buildStoreDetailsHref({ id }),
      },
      { labelKey: actionLabelKey },
    ],
    [id, entityTitle, actionLabelKey],
  );
}

export function useFrozenStationGroupEntityTitle(
  groupId?: string,
): string {
  const params = useParams();
  const id = groupId ?? params.id;
  const [entityTitle, setEntityTitle] = useState("");

  useEffect(() => {
    if (!id) return;
    let active = true;
    StationGroupProfileService.getStationGroupByID(+id)
      .then((res) => {
        if (!active) return;
        const name = pickProfileEntityTitle(res);
        if (name) setEntityTitle(name);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id]);

  return entityTitle;
}

/** Nested station-group tabs — Stations > Groups > Name > Tab */
export function useFrozenStationGroupTabBreadcrumbs(
  actionLabelKey: string,
): BreadcrumbItem[] {
  const { id } = useParams();
  const entityTitle = useFrozenStationGroupEntityTitle();

  return useMemo(
    () => [
      { labelKey: "stations" },
      { labelKey: "stationsGroup", href: "/station-groups" },
      {
        labelKey: "title",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: id ? `/station-group/${id}` : undefined,
      },
      { labelKey: actionLabelKey },
    ],
    [id, entityTitle, actionLabelKey],
  );
}

/** Store rack create/edit — Stations > Stores > Name > Racks > Create|Edit */
export function useFrozenStoreRackFormBreadcrumbs(
  isEdit: boolean,
): BreadcrumbItem[] {
  const { id } = useParams();
  const entityTitle = useFrozenStoreEntityTitle();

  return useMemo(
    () => [
      { labelKey: "stations" },
      { labelKey: "stores", href: "/stores" },
      {
        labelKey: "title",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: buildStoreDetailsHref({ id }),
      },
      {
        labelKey: "racks",
        href: id ? `/store/${id}/racks` : undefined,
      },
      { labelKey: isEdit ? "editRack" : "create" },
    ],
    [id, entityTitle, isEdit],
  );
}
